"use client";

import { SeoEmbeddedLanguageLearningApp } from "@/components/seo/SeoEmbeddedLanguageLearningApp";
import {
  TEACH_DEMO_ROUTES,
  type TeachDemoRoute,
  getTeachDemoRoute,
} from "@/lib/teacher-partners/demoRoutes";
import { useEffect, useState } from "react";

function getDemoRouteFromPathname(pathname: string) {
  const match = pathname.match(/^\/teach\/([^/]+)\/?$/);
  return match ? getTeachDemoRoute(match[1]) : undefined;
}

export function TeacherPartnerDemoSection({
  initialDemoRoute,
}: {
  initialDemoRoute?: TeachDemoRoute;
}) {
  const [demoRoute, setDemoRoute] = useState(initialDemoRoute);
  const pagePath = demoRoute ? `/teach/${demoRoute.slug}` : "/teach";

  useEffect(() => {
    const handlePopState = () => setDemoRoute(getDemoRouteFromPathname(window.location.pathname));
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const selectDemoRoute = (route?: TeachDemoRoute) => {
    const nextPath = route ? `/teach/${route.slug}` : "/teach";
    setDemoRoute(route);
    if (window.location.pathname !== nextPath) {
      window.history.pushState({}, "", nextPath);
    }
  };

  return (
    <section
      id="tutor-demo"
      className="bg-slate-950 border-b border-white/10 py-20 text-white md:py-28"
    >
      <div className="commercial-shell">
        <div className="grid items-end gap-8 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
          <div>
            <p className="text-sm font-black uppercase tracking-[0.12em] text-[#fd954f]">
              Private AI Tutor
            </p>
            <h2 className="mt-4 max-w-[12ch] text-4xl font-black tracking-[-0.045em] md:text-6xl">
              {demoRoute
                ? `Try the ${demoRoute.targetLanguageName} learner experience.`
                : "Try the learner experience in any language."}
            </h2>
          </div>
          <div>
            <p className="max-w-[42rem] text-lg leading-8 text-white/65">
              {demoRoute
                ? `The tutor guides the learner in ${demoRoute.instructionLanguageName} and helps them answer out loud in beginner ${demoRoute.targetLanguageName}.`
                : "Choose a practice language, press Start and answer out loud. This is the same private speaking experience learners can use between live classes."}
            </p>
            <div
              className="mt-6 flex flex-wrap gap-2"
              role="group"
              aria-label="Tutor demo languages"
            >
              <button
                type="button"
                onClick={() => selectDemoRoute()}
                aria-pressed={!demoRoute}
                className={`rounded-full border px-4 py-2 text-sm font-bold transition ${
                  !demoRoute
                    ? "border-[#fd954f] bg-[#fd954f] text-[#171717]"
                    : "border-white/20 text-white/70 hover:border-white/40 hover:text-white"
                }`}
              >
                Any language
              </button>
              {TEACH_DEMO_ROUTES.map((route) => {
                const isActive = demoRoute?.slug === route.slug;
                return (
                  <button
                    key={route.slug}
                    type="button"
                    onClick={() => selectDemoRoute(route)}
                    aria-pressed={isActive}
                    className={`rounded-full border px-4 py-2 text-sm font-bold transition ${
                      isActive
                        ? "border-[#fd954f] bg-[#fd954f] text-[#171717]"
                        : "border-white/20 text-white/70 hover:border-white/40 hover:text-white"
                    }`}
                  >
                    {route.slug === "english" ? "English from Spanish" : route.targetLanguageName}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="mt-10">
          <SeoEmbeddedLanguageLearningApp
            initialTargetLanguageParam={demoRoute?.targetLanguageCode}
            initialInstructionLanguageParam={demoRoute?.instructionLanguageCode}
            pageFamily="teacher_partners"
            pagePath={pagePath}
            requireTargetLanguageSelection={!demoRoute}
          />
        </div>
      </div>
    </section>
  );
}
